import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { useAppBootstrap, type BootstrapTargetOrg } from '../hooks/useAppBootstrap';
import type {
  Organization,
  OrganizationMemberRole,
  UserContext,
} from '../types/organization.types';

interface OrganizationContextValue {
  // User context
  userContext: UserContext | null;
  isLoading: boolean;
  error: string | null;

  // Active organization
  activeOrganization: Organization | null;
  activeRole: OrganizationMemberRole | null;

  // Bootstrap target org (org + subscription + task_count when on org route)
  bootstrapTargetOrg: BootstrapTargetOrg | null;

  // Actions
  setActiveOrganization: (org: Organization | null) => void;
  switchOrganization: (orgId: string) => void;
  refreshUserContext: () => Promise<void>;

  // Helpers
  hasMultipleOrgs: boolean;
  isOrgMember: boolean;
  isSystemAdmin: boolean;
  isOrgUser: boolean;
}

const OrganizationContext = createContext<OrganizationContextValue | undefined>(undefined);

const ACTIVE_ORG_KEY = 'videreo_active_org';

interface OrganizationProviderProps {
  children: ReactNode;
}

export function OrganizationProvider({ children }: OrganizationProviderProps) {
  const [activeOrganization, setActiveOrganizationState] = useState<Organization | null>(null);
  const [activeRole, setActiveRole] = useState<OrganizationMemberRole | null>(null);
  const activeOrgIdRef = useRef<string | null>(null);
  const queryClient = useQueryClient();

  const { data: bootstrapData, isLoading, error, refetch } = useAppBootstrap();
  const userContext = bootstrapData?.userContext ?? null;
  const bootstrapTargetOrg = bootstrapData?.targetOrg ?? null;

  useEffect(() => {
    if (!userContext) {
      if (activeOrgIdRef.current !== null) {
        activeOrgIdRef.current = null;
        setActiveOrganizationState(null);
      }
      setActiveRole(null);
      return;
    }

    // When we have target_org from bootstrap, use it for active org (covers admin + slug case)
    if (bootstrapTargetOrg) {
      const org = bootstrapTargetOrg.organization as Organization;
      if (activeOrgIdRef.current !== org.id) {
        activeOrgIdRef.current = org.id;
        setActiveOrganizationState(org);
        setActiveRole(bootstrapTargetOrg.role as OrganizationMemberRole);
        if (typeof window !== 'undefined') {
          localStorage.setItem(ACTIVE_ORG_KEY, org.id);
        }
      }
      return;
    }

    // Keep active organization stable when still present
    if (activeOrgIdRef.current) {
      const currentMembership = userContext.organizations.find(
        (m) => m.organization.id === activeOrgIdRef.current,
      );
      if (currentMembership) {
        setActiveRole((prevRole) =>
          prevRole === currentMembership.role ? prevRole : currentMembership.role,
        );
        return;
      }
    }

    const savedOrgId = typeof window !== 'undefined' ? localStorage.getItem(ACTIVE_ORG_KEY) : null;
    const savedMembership = userContext.organizations.find(
      (m) => m.organization.id === savedOrgId,
    );

    if (savedMembership) {
      activeOrgIdRef.current = savedMembership.organization.id;
      setActiveOrganizationState((prevOrg) =>
        prevOrg?.id === savedMembership.organization.id ? prevOrg : savedMembership.organization,
      );
      setActiveRole((prevRole) =>
        prevRole === savedMembership.role ? prevRole : savedMembership.role,
      );
      return;
    }

    if (userContext.organizations.length > 0) {
      const firstMembership = userContext.organizations[0];
      activeOrgIdRef.current = firstMembership.organization.id;
      setActiveOrganizationState((prevOrg) =>
        prevOrg?.id === firstMembership.organization.id ? prevOrg : firstMembership.organization,
      );
      setActiveRole((prevRole) =>
        prevRole === firstMembership.role ? prevRole : firstMembership.role,
      );
      if (typeof window !== 'undefined') {
        localStorage.setItem(ACTIVE_ORG_KEY, firstMembership.organization.id);
      }
      return;
    }

    activeOrgIdRef.current = null;
    setActiveOrganizationState(null);
    setActiveRole(null);
  }, [userContext, bootstrapTargetOrg]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
        void queryClient.invalidateQueries({ queryKey: ['app-bootstrap'] });
      } else if (event === 'SIGNED_OUT') {
        activeOrgIdRef.current = null;
        setActiveOrganizationState(null);
        setActiveRole(null);
        if (typeof window !== 'undefined') {
          localStorage.removeItem(ACTIVE_ORG_KEY);
        }
        queryClient.setQueryData(['app-bootstrap'], null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient]);

  const setActiveOrganization = useCallback((org: Organization | null) => {
    activeOrgIdRef.current = org?.id ?? null;
    setActiveOrganizationState(org);

    if (org && userContext) {
      const membership = userContext.organizations.find((m) => m.organization.id === org.id);
      setActiveRole(membership?.role || null);
      if (typeof window !== 'undefined') {
        localStorage.setItem(ACTIVE_ORG_KEY, org.id);
      }
    } else {
      setActiveRole(null);
      if (typeof window !== 'undefined') {
        localStorage.removeItem(ACTIVE_ORG_KEY);
      }
    }
  }, [userContext]);

  const switchOrganization = useCallback(
    (orgId: string) => {
      if (!userContext) return;

      const membership = userContext.organizations.find((m) => m.organization.id === orgId);
      if (membership) {
        setActiveOrganization(membership.organization);
      }
    },
    [userContext, setActiveOrganization],
  );

  const value: OrganizationContextValue = {
    userContext: userContext ?? null,
    isLoading,
    error: error instanceof Error ? error.message : null,
    activeOrganization,
    activeRole,
    bootstrapTargetOrg,
    setActiveOrganization,
    switchOrganization,
    refreshUserContext: async () => {
      await refetch();
    },
    hasMultipleOrgs: ((userContext ?? null)?.organizations.length || 0) > 1,
    isOrgMember: ((userContext ?? null)?.organizations.length || 0) > 0,
    isSystemAdmin: (userContext ?? null)?.isSystemAdmin || false,
    isOrgUser: (userContext ?? null)?.isOrgUser || false,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganizationContext() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganizationContext must be used within an OrganizationProvider');
  }
  return context;
}
